/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ============================================================================
 * Session Timeout Warning Modal
 * ============================================================================
 *
 * Displays a warning modal when the user's session is about to expire.
 * Allows user to extend session or logout immediately.
 *
 * ============================================================================
 */

import { Clock, LogOut, RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface SessionTimeoutWarningProps {
  /** Whether the modal is visible */
  isOpen: boolean;
  /** Minutes remaining before session expires */
  minutesRemaining: number;
  /** Seconds remaining for precise countdown */
  secondsRemaining?: number;
  /** Whether expiry is due to token (vs inactivity) */
  isTokenExpiry?: boolean;
  /** Whether session extension is in progress */
  isExtending?: boolean;
  /** Callback to extend the session */
  onExtendSession: () => void;
  /** Callback to logout immediately */
  onLogout: () => void;
  /** Callback to dismiss the warning */
  onDismiss: () => void;
}

export function SessionTimeoutWarning({
  isOpen,
  minutesRemaining,
  secondsRemaining = 0,
  isTokenExpiry = false,
  isExtending = false,
  onExtendSession,
  onLogout,
  onDismiss,
}: SessionTimeoutWarningProps) {
  const { t } = useTranslation('common');

  if (!isOpen) return null;

  const formatTime = (minutes: number, seconds: number) => {
    if (minutes <= 0 && seconds <= 0) {
      return t('sessionTimeout.time.now');
    }
    if (minutes <= 0) {
      return t('sessionTimeout.time.seconds', { count: seconds });
    }
    if (minutes < 2) {
      // Show minutes and seconds for more precision when under 2 minutes
      const paddedSeconds = seconds.toString().padStart(2, '0');
      return `${minutes}:${paddedSeconds}`;
    }
    return t('sessionTimeout.time.minutes', { count: minutes });
  };

  const expiryReason = isTokenExpiry
    ? t('sessionTimeout.tokenExpiry')
    : t('sessionTimeout.inactivity');

  return (
    <>
      {/* Backdrop */}
      <div className="session-warning-backdrop" onClick={onDismiss} />

      {/* Modal */}
      <div className="session-warning-modal" role="alertdialog" aria-modal="true">
        <div className="session-warning-icon">
          <Clock className="w-8 h-8" />
        </div>

        <h2 className="session-warning-title">{t('sessionTimeout.title')}</h2>

        <p className="session-warning-message">
          {t('sessionTimeout.message', { time: formatTime(minutesRemaining, secondsRemaining) })}{' '}
          {expiryReason}
          {' '}{t('sessionTimeout.stayLoggedInQuestion')}
        </p>

        <div className="session-warning-actions">
          <button
            className="session-warning-btn session-warning-btn-primary"
            onClick={onExtendSession}
            disabled={isExtending}
          >
            <RefreshCw className={`w-4 h-4 ${isExtending ? 'animate-spin' : ''}`} />
            {isExtending ? t('sessionTimeout.extending') : t('sessionTimeout.stayLoggedIn')}
          </button>
          <button
            className="session-warning-btn session-warning-btn-secondary"
            onClick={onLogout}
            disabled={isExtending}
          >
            <LogOut className="w-4 h-4" />
            {t('sessionTimeout.logoutNow')}
          </button>
        </div>

        <p className="session-warning-hint">
          {isTokenExpiry
            ? t('sessionTimeout.hintToken')
            : t('sessionTimeout.hintInactivity')}
        </p>
      </div>

      <style>{sessionWarningStyles}</style>
    </>
  );
}

const sessionWarningStyles = `
  .session-warning-backdrop {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.6);
    backdrop-filter: blur(4px);
    z-index: 9998;
    animation: fadeIn 0.2s ease-out;
  }

  .session-warning-modal {
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    background: var(--color-bg-secondary);
    border: 1px solid var(--color-border-default);
    border-radius: 16px;
    padding: 32px;
    max-width: 420px;
    width: 90%;
    text-align: center;
    z-index: 9999;
    animation: slideIn 0.3s ease-out;
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  }

  .session-warning-icon {
    width: 64px;
    height: 64px;
    border-radius: 50%;
    background: var(--color-warning-light, rgba(245, 158, 11, 0.15));
    color: var(--color-warning, #F59E0B);
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 20px;
  }

  .session-warning-title {
    font-family: var(--font-heading);
    font-size: 20px;
    font-weight: 700;
    color: var(--color-text-primary);
    margin: 0 0 12px;
  }

  .session-warning-message {
    font-size: 14px;
    color: var(--color-text-secondary);
    line-height: 1.6;
    margin: 0 0 24px;
  }

  .session-warning-message strong {
    color: var(--color-warning, #F59E0B);
    font-weight: 600;
  }

  .session-warning-actions {
    display: flex;
    gap: 12px;
    justify-content: center;
  }

  .session-warning-btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 10px 20px;
    font-family: var(--font-heading);
    font-size: 13px;
    font-weight: 600;
    border-radius: 8px;
    border: none;
    cursor: pointer;
    transition: all 0.15s ease;
  }

  .session-warning-btn:hover {
    transform: translateY(-1px);
  }

  .session-warning-btn-primary {
    background: var(--color-brand-primary);
    color: white;
  }

  .session-warning-btn-primary:hover {
    opacity: 0.9;
  }

  .session-warning-btn-secondary {
    background: var(--color-bg-primary);
    color: var(--color-text-primary);
    border: 1px solid var(--color-border-default);
  }

  .session-warning-btn-secondary:hover {
    background: var(--color-bg-hover);
  }

  .session-warning-hint {
    font-size: 12px;
    color: var(--color-text-muted);
    margin: 20px 0 0;
  }

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }

  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translate(-50%, -48%);
    }
    to {
      opacity: 1;
      transform: translate(-50%, -50%);
    }
  }

  @media (max-width: 480px) {
    .session-warning-modal {
      padding: 24px;
    }

    .session-warning-actions {
      flex-direction: column;
    }

    .session-warning-btn {
      width: 100%;
      justify-content: center;
    }
  }
`;

export default SessionTimeoutWarning;
